import { useEffect, useState } from "react";
import { useLang } from "@/contexts/LangContext";
import { ArrowRight, ArrowLeft, X, Sparkles, Tags, Download } from "lucide-react";

const STORAGE_KEY = "gdas_onboarding_seen";

/**
 * One-shot onboarding modal shown to a user the first time they reach the Dashboard.
 * - Persists "seen" flag in localStorage (no backend changes).
 * - Skippable and dismissible by Escape or X.
 * - i18n ES/EN.
 *
 * Usage:
 *   <OnboardingModal />
 * The modal will auto-open on mount IF the flag is not already set.
 */
export default function OnboardingModal() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    try {
      const seen = localStorage.getItem(STORAGE_KEY);
      if (!seen) setOpen(true);
    } catch {
      // localStorage blocked — silently skip
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      // ignore
    }
    setOpen(false);
  };

  if (!open) return null;

  const STEPS = [
    {
      icon: Sparkles,
      title: t("onboarding_step_1_t"),
      body: t("onboarding_step_1_d"),
    },
    {
      icon: Tags,
      title: t("onboarding_step_2_t"),
      body: t("onboarding_step_2_d"),
    },
    {
      icon: Download,
      title: t("onboarding_step_3_t"),
      body: t("onboarding_step_3_d"),
    },
  ];

  const current = STEPS[step];
  const Icon = current.icon;
  const isLast = step === STEPS.length - 1;

  return (
    <div
      data-testid="onboarding-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="onboarding-title"
      className="fixed inset-0 z-50 bg-[#0A0A0A]/80 backdrop-blur-sm flex items-center justify-center p-4 md:p-8"
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div className="w-full max-w-3xl bg-white border border-[#0A0A0A] shadow-2xl relative">
        {/* Close */}
        <button
          data-testid="onboarding-close"
          onClick={close}
          aria-label="close onboarding"
          className="absolute top-4 right-4 w-8 h-8 inline-flex items-center justify-center border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Hero band */}
        <div className="px-8 md:px-12 pt-10 pb-6 border-b border-[#E5E5E5]">
          <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#D90429] mb-3 flex items-center gap-2">
            <span className="w-6 h-px bg-[#D90429]" />
            {t("onboarding_eyebrow")}
          </div>
          <h2
            id="onboarding-title"
            data-testid="onboarding-title"
            className="font-serif-display text-3xl md:text-4xl font-bold tracking-tight leading-none mb-3"
          >
            {t("onboarding_title")}
          </h2>
          <p className="font-sans-body text-sm text-[#525252]">{t("onboarding_lead")}</p>
        </div>

        {/* Step content */}
        <div className="px-8 md:px-12 py-10" data-testid={`onboarding-step-${step + 1}`}>
          <div className="flex items-start gap-6">
            <div className="shrink-0 w-14 h-14 bg-[#D90429] text-white flex items-center justify-center">
              <Icon className="w-7 h-7" />
            </div>
            <div className="flex-1">
              <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-2">
                {t("onboarding_step_n")} {step + 1} {t("onboarding_of")} {STEPS.length}
              </div>
              <h3 className="font-serif-display text-2xl font-bold leading-tight mb-3">
                {current.title}
              </h3>
              <p className="font-sans-body text-base text-[#404040] leading-relaxed">
                {current.body}
              </p>
            </div>
          </div>
        </div>

        {/* Progress + actions */}
        <div className="px-8 md:px-12 py-5 border-t border-[#E5E5E5] flex items-center justify-between gap-4">
          <div className="flex gap-2" data-testid="onboarding-dots">
            {STEPS.map((_, i) => (
              <button
                key={i}
                data-testid={`onboarding-dot-${i + 1}`}
                aria-label={`go to step ${i + 1}`}
                onClick={() => setStep(i)}
                className={`w-2 h-2 transition-colors ${i === step ? "bg-[#D90429]" : "bg-[#E5E5E5] hover:bg-[#A3A3A3]"}`}
              />
            ))}
          </div>
          <div className="flex gap-2">
            <button
              data-testid="onboarding-skip"
              onClick={close}
              className="px-4 py-2 font-mono-tech text-[10px] uppercase tracking-widest text-[#A3A3A3] hover:text-[#0A0A0A]"
            >
              {t("onboarding_skip")}
            </button>
            {step > 0 && (
              <button
                data-testid="onboarding-back"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                className="px-4 py-2 font-mono-tech text-[10px] uppercase tracking-widest border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white inline-flex items-center gap-2"
              >
                <ArrowLeft className="w-3 h-3" /> {t("onboarding_back")}
              </button>
            )}
            <button
              data-testid={isLast ? "onboarding-finish" : "onboarding-next"}
              onClick={() => {
                if (isLast) close();
                else setStep((s) => Math.min(STEPS.length - 1, s + 1));
              }}
              className="px-5 py-2 font-mono-tech text-[10px] uppercase tracking-widest bg-[#D90429] text-white border border-[#D90429] hover:bg-[#0A0A0A] hover:border-[#0A0A0A] inline-flex items-center gap-2"
            >
              {isLast ? t("onboarding_finish") : t("onboarding_next")}
              <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
